// billing-dashboard.component.ts
import { Component, OnInit } from '@angular/core';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { Log, IShortLogger } from '../../../services/logger.service';

interface Invoice {
  invoiceId: string;
  requisitionId: string;
  amount: number;
  status: 'Paid' | 'Pending' | 'Overdue';
  dueDate: string;
}

interface Transaction {
  icon: string;
  amount: number;
  invoiceId: string;
  date: string;
  description: string;
}

interface DashboardSummary {
  totalInvoices: number;
  paid: number;
  pending: number;
  overdue: number;
}
@SidebarNavMenuItem({
  route: '/billing-dashboard',
  faIcon: 'receipt_long',
  name: 'Billing Dashboard',
  order: 1400,
  roles: ['admin']
})
@Component({
  selector: 'app-billing-dashboard',
  template: `
    <div class="dashboard-container">
      <h1>Billing & Accounting Dashboard</h1>

      <!-- Summary Cards -->
      <div class="summary-grid">
        <div class="card bg-blue">
          Total Invoices: ₹{{ summary.totalInvoices | number }}
        </div>
        <div class="card bg-green">
          Paid: ₹{{ summary.paid | number }}
        </div>
        <div class="card bg-yellow">
          Pending: ₹{{ summary.pending | number }}
        </div>
        <div class="card bg-red">
          Overdue: {{ summary.overdue }}
        </div>
      </div>

      <!-- Invoice Table -->
      <div class="card invoice-table-container">
        <h2>Invoice List</h2>
        <table class="invoice-table">
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Requisition ID</th>
              <th>Amount (₹)</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let invoice of invoices">
              <td>{{ invoice.invoiceId }}</td>
              <td>{{ invoice.requisitionId }}</td>
              <td>₹{{ invoice.amount | number }}</td>
              <td>
                <span [class]="'status-' + invoice.status.toLowerCase()">
                  {{ invoice.status }}
                </span>
              </td>
              <td>{{ invoice.dueDate | date:'dd-MMM-yyyy' }}</td>
              <td>
                <button (click)="downloadInvoice(invoice.invoiceId)" class="download-btn">
                  ⬇️
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Recent Transactions -->
      <div class="transactions">
        <h2>Recent Transactions</h2>
        <ul>
          <li *ngFor="let transaction of recentTransactions">
            {{ transaction.icon }} ₹{{ transaction.amount | number }} {{ transaction.description }}
          </li>
        </ul>
      </div>
    </div>
  `,
  styles: [`
    .dashboard-container {
      font-family: Arial, sans-serif;
      padding: 24px;
      background-color: #f9f9f9;
      min-height: 100vh;
    }

    h1 {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 24px;
      color: #1f2937;
    }

    h2 {
      font-size: 20px;
      margin-bottom: 12px;
      color: #374151;
    }

    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 2px;
    }

    .card {
      padding: 16px;
      border-radius: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      font-weight: 500;
    }

    .bg-blue {
      background-color: #dbeafe;
      color: #1e40af;
    }

    .bg-green {
      background-color: #dcfce7;
      color: #166534;
    }

    .bg-yellow {
      background-color: #fef9c3;
      color: #ca8a04;
    }

    .bg-red {
      background-color: #fee2e2;
      color: #dc2626;
    }

    .invoice-table-container {
      margin-bottom: 5px;
    }

    .invoice-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }

    .invoice-table th,
    .invoice-table td {
      border: 1px solid #d1d5db;
      padding: 12px 8px;
      text-align: center;
    }

    .invoice-table th {
      background-color:#204b86;
      font-weight: 600;
      color: white;
    }

    .invoice-table tbody tr:hover {
      background-color: #f9fafb;
    }

    .status-paid {
      color: #166534;
      background-color: #dcfce7;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
    }

    .status-pending {
      color: #ca8a04;
      background-color: #fef9c3;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
    }

    .status-overdue {
      color: #dc2626;
      background-color: #fee2e2;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
    }

    .download-btn {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .download-btn:hover {
      background-color: #f3f4f6;
    }

    .transactions {
      background-color: #f3f4f6;
      padding: 16px;
      border-radius: 16px;
    }

    .transactions ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .transactions li {
      padding: 8px 0;
      border-bottom: 1px solid #e5e7eb;
      color: #374151;
    }

    .transactions li:last-child {
      border-bottom: none;
    }

    @media (max-width: 768px) {
      .dashboard-container {
        padding: 16px;
      }

      .summary-grid {
        grid-template-columns: 1fr;
      }

      .invoice-table {
        font-size: 14px;
      }

      .invoice-table th,
      .invoice-table td {
        padding: 8px 4px;
      }
    }
  `]
})
export class BillingDashboardComponent implements OnInit {
  private readonly logger: IShortLogger<BillingDashboardComponent> = Log.create(BillingDashboardComponent);
  invoices: Invoice[] = [
    {
      invoiceId: 'INV-101',
      requisitionId: 'REQ-001',
      amount: 50000,
      status: 'Paid',
      dueDate: '2025-06-12'
    },
    {
      invoiceId: 'INV-102',
      requisitionId: 'REQ-002',
      amount: 30000,
      status: 'Pending',
      dueDate: '2025-06-18'
    },
    {
      invoiceId: 'INV-103',
      requisitionId: 'REQ-003',
      amount: 45000,
      status: 'Overdue',
      dueDate: '2025-06-01'
    }
  ];

  recentTransactions: Transaction[] = [
    {
      icon: '✓',
      amount: 50000,
      invoiceId: 'INV-101',
      date: '12-Jun',
      description: 'paid for INV-101 on 12-Jun'
    },
    {
      icon: '!',
      amount: 30000,
      invoiceId: 'INV-102',
      date: '',
      description: 'pending for INV-102'
    },
    {
      icon: '✕',
      amount: 45000,
      invoiceId: 'INV-103',
      date: '',
      description: 'overdue for INV-103'
    }
  ];

  summary: DashboardSummary = {
    totalInvoices: 275000,
    paid: 190000,
    pending: 85000,
    overdue: 5
  };

  constructor() { }

  ngOnInit(): void {
    // Initialize component data
    this.calculateSummary();
  }

  downloadInvoice(invoiceId: string): void {
    // Implement download logic here
    this.logger.i(`Downloading invoice: ${invoiceId}`);

    // Example: You could call a service to download the invoice
    // this.invoiceService.downloadInvoice(invoiceId).subscribe(...)

    // For now, just show an alert
    alert(`Downloading invoice ${invoiceId}`);
  }

  private calculateSummary(): void {
    // Recalculate summary based on current invoices
    const paid = this.invoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + inv.amount, 0);

    const pending = this.invoices
      .filter(inv => inv.status === 'Pending')
      .reduce((sum, inv) => sum + inv.amount, 0);

    const overdue = this.invoices
      .filter(inv => inv.status === 'Overdue').length;

    const total = this.invoices
      .reduce((sum, inv) => sum + inv.amount, 0);

    this.summary = {
      totalInvoices: total,
      paid: paid,
      pending: pending,
      overdue: overdue
    };
  }

  // Method to add new invoice (example of extending functionality)
  addInvoice(invoice: Invoice): void {
    this.invoices.push(invoice);
    this.calculateSummary();
  }

  // Method to update invoice status
  updateInvoiceStatus(invoiceId: string, newStatus: 'Paid' | 'Pending' | 'Overdue'): void {
    const invoice = this.invoices.find(inv => inv.invoiceId === invoiceId);
    if (invoice) {
      invoice.status = newStatus;
      this.calculateSummary();
    }
  }
}
